import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../services/api.client'

export interface ClientRecord {
	clientId: string
	name: string
	code: string
	parentClientId: string | null
	productiveCompanyId: string | null
	legalEntity: string | null
	countryRegion: string | null
	isActive: boolean
	createdAt: string
	updatedAt: string
	deletedAt: string | null
}

export interface ProjectRecord {
	projectId: string
	clientId: string
	subClientId: string | null
	code: string
	displayName?: string
	description?: string
	isActive: boolean
	ownerEmail?: string
	pmEmail?: string
	validEnvs?: string[]
	createdAt: string
	updatedAt: string
	deletedAt: string | null
}

// ─── Clients ────────────────────────────────────────────────────────────────

export const useClients = () =>
	useQuery({
		queryKey: ['clients'],
		queryFn: async () => {
			const response = await apiClient.get<ClientRecord[]>('/clients')
			return Array.isArray(response.data) ? response.data : []
		},
	})

export const useCreateClient = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: async (data: {
			name: string
			code: string
			parentClientId?: string | null
			productiveCompanyId?: string | null
			legalEntity?: string | null
			countryRegion?: string | null
		}) => {
			const response = await apiClient.post<ClientRecord>('/clients', data)
			return response.data
		},
		onSuccess: () => void qc.invalidateQueries({ queryKey: ['clients'] }),
	})
}

export const useUpdateClient = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: async ({ id, data }: { id: string; data: Partial<ClientRecord> }) => {
			const response = await apiClient.put<ClientRecord>(`/clients/${id}`, data)
			return response.data
		},
		onSuccess: () => void qc.invalidateQueries({ queryKey: ['clients'] }),
	})
}

export const useDeleteClient = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: async (id: string) => {
			await apiClient.delete(`/clients/${id}`)
		},
		onSuccess: () => void qc.invalidateQueries({ queryKey: ['clients'] }),
	})
}

// ─── Projects ───────────────────────────────────────────────────────────────

export const useProjects = () =>
	useQuery({
		queryKey: ['projects'],
		queryFn: async () => {
			const response = await apiClient.get<ProjectRecord[]>('/projects')
			return response.data || []
		},
	})

export const useProject = (projectId: string) =>
	useQuery({
		queryKey: ['projects', projectId],
		queryFn: async () => {
			const response = await apiClient.get<ProjectRecord>(`/projects/${projectId}`)
			return response.data
		},
		enabled: !!projectId,
	})

export const useCreateProject = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: async (data: {
			clientId: string
			subClientId?: string | null
			code: string
			displayName?: string
			ownerEmail?: string
			pmEmail?: string
			validEnvs?: string[]
		}) => {
			const response = await apiClient.post<ProjectRecord>('/projects', data)
			return response.data
		},
		onSuccess: () => void qc.invalidateQueries({ queryKey: ['projects'] }),
	})
}

export const useUpdateProject = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: async ({ id, data }: { id: string; data: Partial<ProjectRecord> }) => {
			const response = await apiClient.put<ProjectRecord>(`/projects/${id}`, data)
			return response.data
		},
		onSuccess: () => void qc.invalidateQueries({ queryKey: ['projects'] }),
	})
}

export const useDeleteProject = () => {
	const qc = useQueryClient()
	return useMutation({
		mutationFn: async (id: string) => {
			await apiClient.delete(`/projects/${id}`)
		},
		onSuccess: () => void qc.invalidateQueries({ queryKey: ['projects'] }),
	})
}
